import {useEffect} from 'react';
import {WarningOutlined} from '@ant-design/icons';
import {useRouter} from 'next/router';
import {RootStoreType, useStore} from '@/store/root-store';
import {observer} from 'mobx-react-lite';
import {useTranslation} from 'next-i18next';
import styles from './styles.module.scss';
// import {Drawer} from 'antd';
import {EditorState, SelectionState} from 'draft-js';
import {mapFocusTerms, multiWordCountRegex, prepareFinalTerms} from './LeftSide/EditorSection/content-editor/utils';
import {decoratedState} from './LeftSide/EditorSection/content-editor/decorators';
import firebase from 'firebase/app';
import 'firebase/database';
// import {ContentGenerator} from './ContentGenerator/ContentGenerator';
import {LeftSide} from './LeftSide/LeftSide';
// import {AiOutlineDrawer} from './AiOutlineDrawer';
// import {CaAiQuotaInfo} from '@/components/common-components/v2/CaAiQuotaInfo';
// import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
// import {faArrowLeft, faXmark} from '@fortawesome/pro-regular-svg-icons';
import {RightSidebarV2} from './RightSidebar/v2-sidebar';
import {MainLoader} from '@/components/common-components/v2/MainLoader';
import {FREDDY_EVENT_SCA_ARTICLE_CREATED, triggerFreddySurvey} from '@/utils/scripts';
import {headerStore} from '@/store/storeCompTest';
export const PageEditor = observer(
  ({rootStore, isPublic}: { rootStore: RootStoreType; isPublic: boolean }) => {
    const {contentOptimizer, settings} = rootStore;
    const {currentPage, pages, aiOutline: {
      loadAiWriterHistory,
      refetchOneClickDraftResultForArticleCreation,
    }} = contentOptimizer;
    const {
      isUploading,
      isExportingPage,
      isExportingPageLoader,
      setIsPublic,
      // isOpenContentGeneratorDrawer,
      focusTermsClassNames,
      setFocusTermsClassNames,
      // setIsOpenContentGeneratorDrawer,
      setSideBarWidth,
      setViewers,
      viewers,
      isSideBarHidden,
      isInitialEditorStateFetched,
      content,
      editTitle,
      highlights,
    } = currentPage;
    const {aiInProgress} = content;
    const {
      customer: {
        profile: {
          id,
          emailAddress,
        },
        getCustomerQuota,
      },
    } = settings;
    const resultId = contentOptimizer.aiOutline.id;

    const {contentOptimizer: {
      currentPage: {
        feTargetKeywords,
        isLinkActive,
      },
      aiMetaData: {
        loadAiMetaData,
        metaData,
      },
    }} = useStore('');

    useEffect(() => {
      if (pages?.count === 2) {
        triggerFreddySurvey(id, emailAddress, FREDDY_EVENT_SCA_ARTICLE_CREATED);
      }
    }, [pages?.count]);

    // const {setShowOutlinesInDrawer, v2AiOutline, showOutlinesInDrawer} = aiOutline;
    // const [allRef, setAllRef] = useState<HTMLDivElement | null>();

    let htmlToDraft = null;
    if (typeof window === 'object') {
      htmlToDraft = require('html-to-draftjs').default;
    }

    const {
      query: {uuid, newPage},
    } = useRouter();
    const {t} = useTranslation('common');


    // const setAllRefToState = element => {
    //   setAllRef(element);
    // };

    /*
    * CONTENT EDITOR METHODS START
    */


    // We are settings initial focus terms map so we can use it for initial decoration and highlighting

    function moveSelectionToStart(editorState) {
      const content = editorState.getCurrentContent();
      const firstBlock = content.getFirstBlock();
      const firstKey = firstBlock.getKey();

      return EditorState.acceptSelection(
        editorState,
        new SelectionState({
          anchorKey: firstKey,
          anchorOffset: 0,
          focusKey: firstKey,
          focusOffset: 0,
          hasFocus: true,
          isBackward: false,
        }),
      );
    }

    // This method will put cursor to start of the content
    function moveFocusToStart(editorState) {
      const afterSelectionMove = moveSelectionToStart(editorState);
      return EditorState.forceSelection(
        afterSelectionMove,
        afterSelectionMove.getSelection(),
      );
    }


    // This method will do the focus terms re-mapping after we update the page content
    const updateFocusTermsState = () => {
      const sortedFocusTerms = currentPage.analytics?.focusTerms?.slice()
        .slice()
        .sort((a, b) => b.name.length - a.name.length);

      const internalLinksClusters = currentPage.content?.internalLinkSuggestions?.slice()?.map(item => {
        return {
          name: item.keyword,
          status: 'internal-link-suggestion',
          variations: [],
        };
      });

      const finalData = [];
      if (sortedFocusTerms?.length) finalData.push(...sortedFocusTerms);
      if (internalLinksClusters?.length) finalData.push(...internalLinksClusters);

      if (sortedFocusTerms) {
        const parts = prepareFinalTerms(sortedFocusTerms);
        const regex = multiWordCountRegex(parts, false);

        if (regex == null) {
          return;
        }

        setFocusTermsClassNames(mapFocusTerms(finalData));
      }
    };


    useEffect(() => {
      if (uuid && process.env.NODE_ENV === 'production') {
        const dbRef = firebase.database().ref(`${uuid as string}`);
        dbRef.get().then(snapshot => {
          const data = snapshot.val();
          firebase.database().ref(`${uuid as string}`).set(data + 1)
            .then(() => {
              dbRef.on('value', snapshot => {
                if (snapshot.exists()) {
                  const data = snapshot.val();
                  setViewers(data);
                }
              });
            });
        }).catch(error => {
          console.error(error);
        });
      }

      return () => {
        if (uuid && process.env.NODE_ENV === 'production') {
          const dbRef = firebase.database().ref(`${uuid as string}`);
          dbRef.onDisconnect().cancel();
          dbRef.get().then(snapshot => {
            const data = snapshot.val();
            firebase.database().ref(`${uuid as string}`).set(data - 1);
          });
        }
      };
    }, []);

    useEffect(() => {
      if (uuid && process.env.NODE_ENV === 'production') {
        const dbRef = firebase.database().ref(`${uuid as string}`);
        dbRef.onDisconnect().set(viewers - 1);
      }
    }, [viewers]);

    // useEffect(() => {
    //   const newState = currentPage?.content?.formattedEditorState();
    //   currentPage.setCurrentEditorState(newState);
    //   setFocusTermsClassNames(mapFocusTerms(currentPage.analytics?.focusTerms?.slice()));
    // }, []);


    // This useEffect is ment to fire only once initially, after we have contentLoadedAt and uuid set
    // It will do initial content populating + highlighting
    useEffect(() => {
      // this prop is used to allow/dissallow all repolls related to editing a page to happen
      currentPage.setRepollsAllowed(true);
      // we are checking current page uuid from url
      // if the response came in and populated the store they will match and only then we update the state
      // we repeat this both here and in content editor compoenent

      if (uuid === currentPage.content.uuid && isInitialEditorStateFetched) {
        const newState = currentPage.content?.formattedEditorState();
        const focusedState = moveFocusToStart(newState);
        currentPage.setCurrentEditorState(focusedState);

        // This prop is set to false only after initial editor state has been set because
        // we want to prevent the DraftJs from triggering the onChange event on page mount
        currentPage.setIsInitialEditorStateFetched(true);

        // This will re-map focus terms and update the state and will trigger the next useEffect
        // which will use that map to re-decorate the content
        updateFocusTermsState();
      } else {
        const newState = currentPage?.content?.formattedEditorState();
        currentPage.setCurrentEditorState(newState);
        setFocusTermsClassNames(mapFocusTerms(currentPage.analytics?.focusTerms?.slice()));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage.contentLoadedAt, currentPage?.content?.uuid, isInitialEditorStateFetched]);

    useEffect(() => {
      currentPage.setRepollsAllowed(true);
      currentPage.setCurrentEditorState(decoratedState(currentPage.currentEditorState, focusTermsClassNames));
    }, [focusTermsClassNames, highlights]);


    // This is to set initial mapping of the focus terms, which is used for decorating
    useEffect(() => {
      updateFocusTermsState();
    }, [JSON.stringify(currentPage.analytics?.focusTerms?.slice()), JSON.stringify(currentPage.content?.internalLinkSuggestions?.slice())]);

    const loadProject = async () => {
      await pages.getAllProjects();
    };

    useEffect(() => {
      if (!isPublic) loadProject();
      if (isPublic) {
        setIsPublic(true);
      } else {
        setIsPublic(false);
      }
    }, []);

    useEffect(() => {
      if (uuid && !isPublic) {
        loadAiWriterHistory(uuid as string);
      }
    }, []);

    useEffect(() => {
      if (resultId) {
        setRefechForBulkArticleCreation();
      }
    }, [resultId, aiInProgress]);

    const setRefechForBulkArticleCreation = async () => {
      if (content.aiInProgress && resultId) {
        try {
          const now = new Date().getTime();
          await refetchOneClickDraftResultForArticleCreation(uuid, now);
        } catch (e) {
          Promise.reject(e);
        } finally {
          getCustomerQuota();
        }
      }
    };

    useEffect(() => {
      async function load() {
        if (uuid) {
          if (!isPublic) {
            pages.loadPages(false, pages.activeProjectId);
          }
          // this is to check if we are on initial page create screen or are we editing page
          if (!newPage) {
            await currentPage.loadPage(uuid as string);
            const targetKwArr = feTargetKeywords.map(kw => kw.keyword);
            if (content.title?.startsWith('New Document') && targetKwArr?.length) {
              const data = await loadAiMetaData(content.title, targetKwArr);
              if (data) {
                editTitle(data[0]?.metaTitle);
              }
            }
          } else {
            currentPage.initialLoadPage(uuid as string);
          }
        }
      }
      load();
    }, [uuid, currentPage.currentStep, metaData]);

    useEffect(() => {
      if (newPage) {
        currentPage.setNewPage(true);
        currentPage.setCurrentStep(1);
      } else {
        currentPage.setNewPage(false);
      }
    }, [newPage]);

    useEffect(()=> {
      headerStore.setComponent(null, false, [
        {name: '', spend: 0, total: 0, color: 'green', quotaProps: null},
      ]);
    }, []);

    const callBack = (width: number) => {
      if (width <= 400 && width > 0) {
        setSideBarWidth(`${width}px`);
      }
    };


    return currentPage.loading ? (
      <MainLoader />
    ) : isUploading ? (
      <MainLoader />
    ) : (
      <>
        {isExportingPageLoader && <>
          <div className={styles.loaderSpinner}></div>
          <div className={styles.loaderSpinnerContainer}></div>
        </>}
        <div className={styles.container}
          style={{display: isLinkActive ? 'block' : 'flex',
            height: isExportingPage ?
              `calc(${(document.querySelector('.DraftEditor-root') as HTMLElement)?.offsetHeight}px + 500px)` : '99%',
          }}
        >


          {!currentPage.error ? (
            <>
              <LeftSide
              // do not remove yet
              // allRef={allRef}
                newPage={newPage} htmlToDraft={htmlToDraft}/>
              {!currentPage.isLinkActive && ((currentPage.newPage && currentPage.currentStep === 1) || <>
                <div style={{backgroundColor: '#fff'}}>
                  <RightSidebarV2 onResizeCallback={callBack} isHidden={isSideBarHidden} store={currentPage} />
                </div>
              </>)
              }
            </>
          ) : (
            <div className={styles.pageEditorError}>
              <WarningOutlined />
              <h1>{t('error-has-ocurred')}</h1>
            </div>
          )}
        </div>
      </>
    );
  },
);
